import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

/**
 * InboundTriagePanel — surfaces every inbound member reply across orders.
 * Pulls from GET /api/admin/inbound-triage.
 */
export default function InboundTriagePanel() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    api
      .get("/admin/inbound-triage", { params: { limit: 25 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="surface-card p-8 mt-10" data-testid="inbound-triage">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">Inbound · Triage</div>
          <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Member Replies (Email)</h2>
          <p className="text-sm text-[#7A7A7A] mt-1">
            Every reply Resend has routed back into an order. Click a row for
            the full preview.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="inbound-triage-refresh"
          className="btn-secondary text-xs"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">
          Could not load inbound triage.
        </div>
      )}

      {items === null && !error && (
        <div className="mt-6 overline text-[#7A7A7A]">Loading…</div>
      )}

      {items && items.length === 0 && (
        <div className="mt-6 border border-dashed border-[#E5E3D8] p-8 text-center">
          <div className="overline">Inbox clear</div>
          <p className="font-serif text-xl mt-2 text-[#1A1A1A]">No member replies yet.</p>
          <p className="text-sm text-[#7A7A7A] mt-1">
            Replies to pro-shop emails will appear here automatically.
          </p>
        </div>
      )}

      {items && items.length > 0 && (
        <ul className="mt-6 divide-y divide-[#E5E3D8] border border-[#E5E3D8]" data-testid="inbound-triage-list">
          {items.map((row) => {
            const id = row.resend_received_id || `${row.order_id}-${row.at}`;
            const isOpen = expanded === id;
            return (
              <li key={id} className="bg-white" data-testid={`inbound-row-${row.order_id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : id)}
                  className="w-full text-left px-6 py-4 flex items-start justify-between gap-4 hover:bg-[#FAF9F2] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg text-[#1A1A1A] truncate">
                      {row.subject || "(no subject)"}
                    </div>
                    <div className="text-xs text-[#7A7A7A] uppercase tracking-wider mt-1">
                      {row.member_name || "Unknown"} · {row.item_name} ·{" "}
                      <span className="font-mono text-[#1A422D] normal-case tracking-normal">{row.order_id}</span>
                    </div>
                    <div className="text-xs text-[#7A7A7A] mt-1">
                      from {row.from || "?"} · {new Date(row.at).toLocaleString()}
                    </div>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-[#1A422D] flex-shrink-0 mt-1">
                    {isOpen ? "Hide ↑" : "View ↓"}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 pt-1 bg-[#FAF9F2]">
                    <div className="border-l-2 border-[#B59A5A] pl-4 my-3">
                      <div className="font-serif text-sm leading-relaxed whitespace-pre-wrap text-[#1A1A1A]">
                        {row.body_preview || "(empty)"}
                      </div>
                    </div>
                    <Link
                      to={`/orders/${row.order_id}`}
                      className="text-xs uppercase tracking-wider text-[#1A422D] underline underline-offset-4"
                    >
                      Open order →
                    </Link>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
